import * as React from "react"
import { Col, Hidden, Row, Visible } from "react-grid-system"
import styled from "styled-components"
import H2 from "../../../atoms/typography/H2"
import H4 from "../../../atoms/typography/H4"
import { HorizontalSpacer } from "../../../atoms/spacers"
import H3 from "../../../atoms/typography/H3"
import Counter from "../../../molecules/Counter"
import WarrantySelectionButton from "../../../molecules/store/CustomizeAndOrder/WarrantySelectionButton"
import TextButton from "../../../atoms/buttons/TextButton"
import { ArrowDropDownIcon } from "../../../atoms/icons/ArrowDropDownIcon"
import SmallColorfulButton from "../../../atoms/buttons/SmallColorfulButton"
import { ArrowRight } from "../../../atoms/icons/ArrowRight"
import { useAllModems } from "../../../../hooks-saleor/useAllModems"
import { getWarranty } from "../../../../hooks-saleor/getWarrantyQuery"
import LoadingSpinner from "../../../atoms/LoadingSpinner"
import { useGetTerminalQuery } from "../../../../hooks-saleor/getTerminalQuery"
import { getFormattedPrice } from "../../../../utils/cart-helpers"
import { Product } from "../../../interfaces/SaleorTypes"
import Body from "../../../atoms/typography/Body"
import { SlideOutHeight } from "../../../atoms/animations/SlideOutHeight"
import { ArrowDropUpIcon } from "../../../atoms/icons/ArrowDropUpIcon"
import { theme } from "../../../theme"
import { IQ200_MODEM_SLUG } from "../../../../utils/saleor-constants"
import { captureExceptionWithContext } from "../../../../utils/sentry"

interface DefaultValues {
  numberOfWarranties?: number
  terminalAmount?: number
  selectedModem?: Product
}

interface Props {
  onClickNextStep: (
    terminal: Product,
    tA: number,
    eW: number,
    m: Product | undefined,
    terminalTotal: number,
    hideDataPlans?: boolean,
  ) => void
  defaultValues?: DefaultValues
  terminalType: "ku" | "secure"
}

const ProductImage = styled.img`
  width: 100%;
  object-fit: contain;
`

const FlexedText = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const ModemSelect = styled.select`
  outline: none;
  border: none;
  font-size: 16px;
  color: ${(props) => props.theme.colors.greyDark};
  font-family: "Karla";
  background-color: transparent;
`

const CustomizeTerminals: React.FC<Props> = ({ onClickNextStep, defaultValues, terminalType }) => {
  const [showProductInfo, setShowProductInfo] = React.useState(false)

  //Selecting modem
  const { modems, loading: modemsLoading, error: modemsError } = useAllModems()
  const modemAlternatives: Array<{ slug: string; value?: string; title: string }> = modems?.map(
    (modem: { node: { slug: string; name: string } }) => {
      return { slug: modem.node.slug, title: modem.node.name }
    },
  )
  const [selectedModem, setSelectedModem] = React.useState<undefined | string>(defaultValues?.selectedModem?.slug)
  const [showModemWarning, setShowModemWarning] = React.useState(
    selectedModem === undefined ? false : selectedModem !== IQ200_MODEM_SLUG,
  )

  const onSelectModem = (slug: string, name: string) => {
    if (slug !== IQ200_MODEM_SLUG) {
      setShowModemWarning(true)
    } else {
      setShowModemWarning(false)
    }
    setSelectedModem(slug)
  }

  //Selecting warranties
  const { warranty, loading: warrantyLoading, error: warrantyError } = getWarranty()
  const [selectedWarranty, setSelectedWarranty] = React.useState(defaultValues?.numberOfWarranties ?? 0)

  //Selecting terminal
  const [amount, setAmount] = React.useState(defaultValues?.terminalAmount ?? 1)
  const { terminals, loading: terminalsLoading } = useGetTerminalQuery()
  const isKu = terminalType === "ku"

  if (terminalsLoading || modemsLoading || warrantyLoading) {
    return <LoadingSpinner />
  }

  // TODO: Add proper error handling for the end user
  if (!terminals) {
    captureExceptionWithContext(new Error("Terminals data is missing"), "Error fetching terminals")
    return <Body color={theme.colors.redDark}>Error: Terminals data is missing.</Body>
  }
  const selectedTerminal: Product = isKu ? terminals[0] : terminals[1]

  const onClickNext = (hideDataplans?: boolean) => {
    const modemProduct =
      modems && selectedModem === undefined
        ? modems[0]
        : modems.find((modem: { node: { id: string; name: string } }) => modem.node.id === selectedModem)

    if (!isKu) {
      onClickNextStep(selectedTerminal, amount, selectedWarranty, undefined, currentTotal, false)
    } else if (hideDataplans === true) {
      onClickNextStep(selectedTerminal, amount, selectedWarranty, modemProduct, currentTotal, true)
    } else {
      onClickNextStep(selectedTerminal, amount, selectedWarranty, modemProduct, currentTotal, false)
    }
  }

  const currentTotal =
    selectedTerminal.variants[0].pricing.price.gross.amount * amount +
    warranty.variants[0].pricing.price.gross.amount * selectedWarranty * amount

  const image = selectedTerminal.media[0]?.url

  if (modemsError) {
    captureExceptionWithContext(modemsError, "Error fetching modems")
  }
  if (warrantyError) {
    captureExceptionWithContext(warrantyError, "Error fetching warranties")
  }

  return (
    <div>
      <Row nogutter>
        <Col lg={1} />
        <Col lg={6}>
          <ProductImage src={image} />
        </Col>
        <Col lg={4}>
          <FlexedText>
            <H2>{selectedTerminal?.name}</H2>
            <H4>
              {getFormattedPrice(
                selectedTerminal?.variants[0]?.pricing.price.gross.amount ?? 0,
                selectedTerminal?.variants[0]?.pricing.price.gross.currency,
              )}
            </H4>
          </FlexedText>
          <HorizontalSpacer spacing={1.5} />
          <FlexedText>
            <H3>Quantity:</H3>
            <Counter
              currentValue={amount}
              minValue={1}
              onClickAdd={() => setAmount(amount + 1)}
              onClickMinus={() => setAmount(amount - 1)}
            />
          </FlexedText>
          <HorizontalSpacer spacing={1.5} />
          <FlexedText>
            {isKu ? <H3>Select modem:</H3> : <H3>Modem:</H3>}
            {modemsLoading ? (
              <LoadingSpinner />
            ) : (
              <>
                {isKu ? (
                  <div>
                    <ModemSelect
                      name="roleType"
                      onChange={(e) => onSelectModem(e.target.value, e.target.name)}
                      defaultValue={selectedModem}
                    >
                      {modemAlternatives?.map((modem) => (
                        <option value={modem.slug} key={modem.slug}>
                          {modem.title}
                        </option>
                      ))}
                    </ModemSelect>
                  </div>
                ) : (
                  <Body>iDirect 950MP</Body>
                )}
              </>
            )}
          </FlexedText>
          {showModemWarning && isKu && (
            <div>
              <HorizontalSpacer spacing={1.5} />
              <Body color={theme.colors.redDark}>Important information:</Body>
              <Body color={theme.colors.redDark}>
                Data plans can only be purchased online if modem IQ200 is selected!
              </Body>
            </div>
          )}
          <HorizontalSpacer spacing={1.5} />
          <H3>Extended warranty:</H3>
          <HorizontalSpacer spacing={1} />
          {warrantyLoading ? (
            <LoadingSpinner />
          ) : (
            <>
              <WarrantySelectionButton
                selected={selectedWarranty === 0}
                onClick={() => setSelectedWarranty(0)}
                title="1 year warranty"
                price="Included"
                gradient={isKu ? theme.gradients.blueBorder : theme.gradients.greenBorder}
              />
              <HorizontalSpacer spacing={1} />
              <WarrantySelectionButton
                selected={selectedWarranty === 1}
                onClick={() => setSelectedWarranty(1)}
                title="2 year warranty"
                price={`${getFormattedPrice(
                  warranty.variants[0].pricing.price.gross.amount,
                  warranty.variants[0]?.pricing.price.currency,
                )}`}
                gradient={isKu ? theme.gradients.blueBorder : theme.gradients.greenBorder}
              />
              <HorizontalSpacer spacing={1} />
              <WarrantySelectionButton
                selected={selectedWarranty === 2}
                onClick={() => setSelectedWarranty(2)}
                title="3 year warranty"
                price={`${getFormattedPrice(
                  warranty.variants[0].pricing.price.gross.amount * 2,
                  warranty.variants[0]?.pricing.price.currency,
                )}`}
                gradient={isKu ? theme.gradients.blueBorder : theme.gradients.greenBorder}
              />
            </>
          )}
          <HorizontalSpacer spacing={1.5} />
          <TextButton
            title="Product information"
            icon={showProductInfo ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
            onClick={() => setShowProductInfo(!showProductInfo)}
          />
          <SlideOutHeight isVisible={showProductInfo}>
            <HorizontalSpacer spacing={1.5} />
            <Body>
              {isKu
                ? "The Satcube Ku has a highly intuitive interface, enabling users to " +
                  "create a Wi-Fi hotspot and get a high-speed broadband connection in less than a minute."
                : "The Satcube Secure enables mission-critical communications thanks to its full encryption capabilities. " +
                  "Its highly intuitive interface enables users to get a high-speed internet connection in less than a minute."}
              <br />
              <br />
              Included with purchase is three batteries, one pelican hardcase, one external charger and one power
              supply.
            </Body>
          </SlideOutHeight>
          <HorizontalSpacer spacing={1.5} />
          <FlexedText>
            <div>
              <H4>Total:</H4>
              <H4>{getFormattedPrice(currentTotal, warranty.variants[0].pricing.price.currency)}</H4>
            </div>
            <Hidden xs>
              {showModemWarning && isKu ? (
                <SmallColorfulButton
                  title="Next step: Accessories"
                  iconRight={<ArrowRight color="white" />}
                  onClick={() => onClickNext(true)}
                />
              ) : (
                <SmallColorfulButton
                  title="Next step: Data plan"
                  iconRight={<ArrowRight color="white" />}
                  onClick={() => onClickNext(false)}
                  variant={isKu ? "blue" : "green"}
                />
              )}
            </Hidden>
          </FlexedText>
          <Visible xs>
            <HorizontalSpacer spacing={3} />
            {showModemWarning && isKu ? (
              <SmallColorfulButton
                title="Next step: Accessories"
                iconRight={<ArrowRight color="white" />}
                onClick={() => onClickNext(true)}
              />
            ) : (
              <SmallColorfulButton
                title="Next step: Data plan"
                iconRight={<ArrowRight color="white" />}
                onClick={() => onClickNext(false)}
                variant={isKu ? "blue" : "green"}
              />
            )}
          </Visible>
        </Col>
      </Row>
    </div>
  )
}

export default CustomizeTerminals
